"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var widgets_1 = require("@tableau/widgets");
var PrepDetailsChangesEdit_1 = require("../../gen-src/icons/PrepDetailsChangesEdit");
var Localize_1 = require("../../gen-src/Localize");
var Utilities_1 = require("../Utilities");
function makeStateMachine(editorStateWhenEnteringEditMode) {
    return function inlineEditorStateMachine(prevState, action) {
        if (prevState.inEditMode) {
            switch (action.type) {
                case 'set-editor-state-within-edit-mode':
                    // Prevent the client from messing with the state during the async save process
                    if (prevState.isActionBusy) {
                        return prevState;
                    }
                    var nextEditorState = typeof action.nextEditorState === 'function'
                        ? action.nextEditorState(prevState.editorState)
                        : action.nextEditorState;
                    return __assign(__assign({}, prevState), { editorState: nextEditorState });
                case 'start-save':
                    return __assign(__assign({}, prevState), { isActionBusy: true });
                case 'abort-save':
                    return __assign(__assign({}, prevState), { isActionBusy: false });
                case 'finish-save':
                case 'cancel':
                    return { editorState: undefined, isActionBusy: false, inEditMode: false };
                case 'start-editing':
                    return prevState;
                default:
                    Utilities_1.assertUnreachable(action);
                    return prevState;
            }
        }
        else {
            switch (action.type) {
                case 'start-editing':
                    return __assign(__assign({}, prevState), { editorState: editorStateWhenEnteringEditMode, inEditMode: true });
                case 'set-editor-state-within-edit-mode':
                case 'start-save':
                case 'finish-save':
                case 'abort-save':
                case 'cancel':
                    return __assign(__assign({}, prevState), { editorState: undefined });
                default:
                    Utilities_1.assertUnreachable(action);
                    return prevState;
            }
        }
    };
}
/**
 * The useInlineEditor hook provides state-handling logic for an inline editor scenario
 * (examples: rename a content item or edit its description on its place page, rather than a dedicated modal dialog).
 *
 * This editor allows for two modes:
 *
 * - Readonly mode: Show the data in a readonly mode, with a button that, when clicked, enters edit mode
 * - Edit mode: Provide widgets that allow the user to edit the data, with a save button that, when clicked,
 *   sends the new data to the onSave callback provided by the client.
 *
 * This hook provides the required state, callbacks, and button components to facilitate these two modes,
 * but the client component is expected to provide their actual layout/implementation.
 * See the unit tests in useInlineEditor.test.tsx for a simple usage example.
 *
 * @param params The params passed into the hook. See documentation for InlineEditorParams.
 */
function useInlineEditor(params) {
    var _this = this;
    var stateMachine = React.useMemo(function () { return makeStateMachine(params.editorStateWhenEnteringEditMode); }, [params.editorStateWhenEnteringEditMode]);
    var _a = React.useReducer(stateMachine, {
        editorState: undefined,
        isActionBusy: false,
        inEditMode: false,
    }), state = _a[0], dispatch = _a[1];
    var setEditorStateWithinEditMode = React.useCallback(function (setStateAction) { return dispatch({ type: 'set-editor-state-within-edit-mode', nextEditorState: setStateAction }); }, []);
    var cancel = React.useCallback(function () {
        dispatch({ type: 'cancel' });
        if (params.onCancel) {
            params.onCancel(state.editorState);
        }
    }, [params.onCancel, state.editorState]);
    var enterEditMode = React.useCallback(function () {
        if (params.canEdit) {
            dispatch({ type: 'start-editing' });
        }
    }, [params.canEdit]);
    var save = React.useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    dispatch({ type: 'start-save' });
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, params.onSave(state.editorState)];
                case 2:
                    _b.sent();
                    return [4 /*yield*/, Utilities_1.delayPromise(100)];
                case 3:
                    _b.sent();
                    dispatch({ type: 'finish-save' });
                    return [3 /*break*/, 5];
                case 4:
                    _a = _b.sent();
                    if (params.exitEditModeOnFailedSave) {
                        dispatch({ type: 'cancel' });
                    }
                    else {
                        dispatch({ type: 'abort-save' });
                    }
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [params.onSave, params.exitEditModeOnFailedSave, state.editorState]);
    var canSave = React.useMemo(function () {
        if (state.inEditMode) {
            return params.canSave ? params.canSave(state.editorState) : true;
        }
        else {
            return false;
        }
    }, [state.inEditMode, state.editorState, params.canSave]);
    var saveButton = (React.createElement(widgets_1.ButtonWidget, { disabled: !canSave || state.isActionBusy, buttonType: widgets_1.ButtonType.Primary, handleClick: save, title: Localize_1.Messages.Save(), testId: "inline-editor-save" }, state.isActionBusy ? (React.createElement("div", { style: { marginTop: widgets_1.TabStyles.Spacing.SmallSpace } },
        React.createElement(widgets_1.ActivitySpinnerWidget, { onDarkBackground: true, spinnerSize: widgets_1.ActivitySpinnerSize.Small, testId: "inline-editor-saving" }))) : (React.createElement("span", null, Localize_1.Messages.Save()))));
    var cancelButton = (React.createElement(widgets_1.ButtonWidget, { disabled: state.isActionBusy, buttonType: widgets_1.ButtonType.Outline, handleClick: cancel, title: Localize_1.Messages.Cancel(), testId: "inline-editor-cancel" }, Localize_1.Messages.Cancel()));
    var editButton = (React.createElement(widgets_1.ButtonWidget, { style: !params.canEdit ? { visibility: 'hidden' } : undefined, disabled: !params.canEdit, buttonType: widgets_1.ButtonType.Icon, handleClick: enterEditMode, title: params.editButtonLabel, testId: "inline-editor-icon" },
        React.createElement(PrepDetailsChangesEdit_1.SvgPrepDetailsChangesEdit, null)));
    if (state.inEditMode) {
        return {
            editorState: state.editorState,
            setEditorStateWithinEditMode: setEditorStateWithinEditMode,
            isActionBusy: state.isActionBusy,
            inEditMode: true,
            saveButton: saveButton,
            cancelButton: cancelButton,
        };
    }
    else {
        return {
            inEditMode: false,
            editButton: editButton,
            enterEditMode: enterEditMode,
        };
    }
}
exports.useInlineEditor = useInlineEditor;
function isEditMode(logic) {
    return logic.inEditMode;
}
exports.isEditMode = isEditMode;
function isReadonlyMode(logic) {
    return !logic.inEditMode;
}
exports.isReadonlyMode = isReadonlyMode;
//# sourceMappingURL=useInlineEditor.js.map